<template>
    <div :id="'rich-content-' + _uid">
        <div v-for="(element, index) in elements" :key="element.id">
            <!-- Image -->
            <div v-if="['image', 'image_ai'].includes(element.element_type) && element.stored_file">
                <!-- Legacy Video Player - No Longer Used -->
                <div v-if="element.stored_file.content_type === 'video/mp4'">
                    <video
                        controls
                        :loop="element.loop_media"
                        :autoplay="element.autoplay_video"
                        class="image-inline"
                        :style="{ width: `${element.display_width}%` }"
                    >
                        <source :src="element.stored_file.signed_url" type="video/mp4" />
                        Your browser doesn't support embedded videos.
                    </video>
                </div>
                <!-- Images -->
                <div
                    v-else
                    class="position-relative"
                    :class="{
                        'd-flex justify-content-center': element.display_justify === 'center',
                    }"
                >
                    <div
                        @mouseover="hoverElement(element)"
                        @mouseleave="unhoverElement(element)"
                        class="position-relative mw-100"
                        :data-resize="_uid"
                        :style="imageStyleBlock(element)"
                        :data-id="element.id"
                    >
                        <img
                            :src="element.stored_file.signed_url"
                            class="expandable-image w-100"
                            :class="imageAndVideoElClassObj(element)"
                            :alt="element.accessibility_alt_text"
                            v-on:load="imageLoaded()"
                        />
                        <!-- Expanded/Fullscreen Image -->
                        <div
                            v-if="element.isExpanded"
                            class="image-expanded"
                            @click="toggleExpandedImage(element, false)"
                        >
                            <div class="image-container">
                                <img
                                    :src="element.stored_file.signed_url"
                                    :alt="element.accessibility_alt_text"
                                    class="rounded"
                                />
                            </div>
                            <button class="btn btn-icon close-button">
                                <span class="icon icon-100 icon-glyphicons-basic-menu-close align-middle"></span>
                            </button>
                        </div>
                        <!-- Image Resize Handles -->
                        <div v-if="editable && element.editIsActive">
                            <div class="resize-handle handle-top-left"></div>
                            <div class="resize-handle handle-top-right"></div>
                            <div class="resize-handle handle-bottom-left"></div>
                            <div class="resize-handle handle-bottom-right"></div>
                        </div>
                        <!-- Image Edit Toolbar -->
                        <div
                            class="rounded element-toolbar d-flex"
                            :class="{ visible: element.isHover || element.editIsActive }"
                        >
                            <!-- Jusifty Buttons -->
                            <div
                                class="btn-group mr-1"
                                role="group"
                                aria-label="First group"
                                v-show="editable && element.editIsActive"
                                :class="{ 'shadow-sm': element.isHover }"
                            >
                                <button
                                    type="button"
                                    class="btn btn-dark btn-sm"
                                    :class="{ active: element.display_justify === 'left' }"
                                    @click.stop="setElementJustify(element, 'left')"
                                >
                                    <span class="icon icon-lg icon-glyphicons-basic-paragraph-left align-middle"></span>
                                </button>
                                <button
                                    type="button"
                                    class="btn btn-dark btn-sm"
                                    :class="{ active: element.display_justify === 'center' }"
                                    @click.stop="setElementJustify(element, 'center')"
                                >
                                    <span
                                        class="icon icon-lg icon-glyphicons-basic-paragraph-center align-middle"
                                    ></span>
                                </button>
                            </div>
                            <!-- Enter Edit Button -->
                            <info-popover content="Edit image" placement="top" :delay="600">
                                <button
                                    v-show="editable && !element.editIsActive"
                                    @click.stop="enterImageEditMode(element)"
                                    type="button"
                                    class="btn btn-dark btn-sm shadow-sm"
                                >
                                    <span
                                        class="icon icon-lg icon-glyphicons-basic-vector-path-edit align-middle"
                                    ></span>
                                </button>
                            </info-popover>
                            <!-- Exit Edit Button -->
                            <button
                                v-show="editable && element.editIsActive"
                                @click.stop="exitImageEditMode(element)"
                                type="button"
                                class="btn btn-dark btn-sm shadow-sm"
                            >
                                <span class="icon icon-lg icon-glyphicons-basic-check align-middle"></span>
                            </button>
                            <!-- Open Expanded Image Button -->
                            <button
                                v-show="!editable"
                                type="button"
                                class="btn btn-dark btn-sm shadow-sm"
                                @click.stop="toggleExpandedImage(element, true)"
                            >
                                <img src="/static/general/img/icons/maximize.svg" height="15" class="align-middle" />
                            </button>
                        </div>
                    </div>
                </div>
            </div>
            <!-- Video Players -->
            <div v-else-if="['youtube', 'vimeo', 'video'].includes(element.element_type)" class="position-relative">
                <!-- Video Overlay -->
                <div
                    class="video-cover"
                    :class="videoCoverClassObj(element)"
                    v-show="!element.continue_after_video_finished"
                ></div>
                <!-- Replay & Close Button -->
                <div
                    :class="{
                        'replay-continue-btns': element.videoComplete,
                        'full-screen': element.display_full_screen,
                        'video-overlay': !element.display_full_screen,
                        'd-none': element.continue_after_video_finished && !skipMediaToEnd,
                    }"
                    v-show="element.videoComplete && !element.loop_media"
                >
                    <div class="w-100 h-100 d-flex flex-column default-cursor">
                        <div class="flex-grow-1 d-flex align-items-center justify-content-center">
                            <button class="btn btn-light restart-btn" @click="restartVideo(element, index)">
                                <span class="icon icon-glyphicons-basic-restart icon-mr"></span>
                                <span>{{ labelsTranslated.replay }}</span>
                            </button>
                            <button
                                v-show="showContinueButtonOnVideos && element.display_full_screen"
                                class="btn btn-brand restart-btn ml-2"
                                @click="continueToNext()"
                                :disabled="videoContinueLoading"
                            >
                                <span
                                    v-show="videoContinueLoading"
                                    class="spinner-grow spinner-grow-sm mr-2"
                                    role="status"
                                    aria-hidden="true"
                                ></span>
                                <span>{{ continueBtnText }}</span>
                            </button>
                        </div>
                    </div>
                </div>
                <!-- Centered Pause Button -->
                <div
                    v-if="
                        (element.hide_video_controls && element.element_type == 'vimeo') ||
                        element.element_type === 'video'
                    "
                    class="play-pause-btn"
                    :class="{
                        'full-screen': element.display_full_screen,
                        'video-overlay': !element.display_full_screen,
                        'default-cursor': element.element_type == 'vimeo' && !element.display_full_screen,
                        'opacity-1': element.isPlaying === false && !element.videoComplete,
                    }"
                    @click="togglePauseVideo(element)"
                >
                    <div class="w-100 h-100 d-flex flex-column">
                        <div class="flex-grow-1 d-flex align-items-center justify-content-center">
                            <span class="icon align-middle icon-glyphicons-basic-play centered-play-pause-icon"></span>
                        </div>
                    </div>
                </div>
                <!-- Video Player -->
                <div class="hide-overflow rounded-default" :class="imageAndVideoElClassObj(element)">
                    <!-- YouTube -->
                    <you-tube-video
                        v-if="element.content && element.element_type == 'youtube'"
                        v-on:video-finished="handleVideoComplete(element)"
                        :video-url="element.content"
                        :autoplay="element.autoplay_video"
                        :height="element.display_full_screen ? null : element.height"
                        :class="{
                            'embedded-video-blurred': element.videoComplete,
                            'full-screen': element.display_full_screen,
                            'disable-pointers': disableIframePointers,
                        }"
                        class="embedded-video"
                        :ref="'youtube-video' + element.id"
                    ></you-tube-video>
                    <!-- Vimeo -->
                    <div v-if="element.content && element.element_type == 'vimeo'" class="default-cursor bg-white">
                        <!-- Vimeo Player -->
                        <div
                            class="w-100"
                            :id="'vimeo' + element.id"
                            :class="{
                                'full-screen vimeo-fullscreen': element.display_full_screen,
                                'vimeo-preloaded': !element.isLoaded,
                                'disable-pointers': disableIframePointers,
                            }"
                        ></div>
                        <!-- Video Control Buttons -->
                        <div
                            class="d-flex justify-content-end align-items-center video-btn-container"
                            :class="{
                                'position-fixed': element.display_full_screen,
                                'position-absolute': !element.display_full_screen,
                            }"
                            v-if="element.isLoaded"
                        >
                            <div
                                v-show="element.continueWasEmitted && element.continue_after_video_finished"
                                class="spinner-grow spinner-grow-md text-light mr-2"
                                role="status"
                            >
                                <span class="sr-only">Loading...</span>
                            </div>
                            <info-popover
                                v-if="element.hide_video_controls && !element.loop_media"
                                :content="getProgressBarHoverText(element)"
                                placement="top"
                            >
                                <div class="progress video-progress">
                                    <div
                                        class="progress-bar bg-primary video-progress-ease"
                                        role="progressbar"
                                        :style="{ width: element.videoProgressPercent + '%' }"
                                        aria-valuemin="0"
                                        aria-valuemax="100"
                                        aria-label="Video progress"
                                    ></div>
                                </div>
                            </info-popover>
                            <div
                                v-if="element.hide_video_controls"
                                class="p-1 mr-2 rounded pointer video-btn"
                                @click.stop="togglePauseVideo(element)"
                                role="button"
                                aria-label="Toggle video play/pause"
                            >
                                <span
                                    class="icon text-light video-btn-icon pointer align-middle"
                                    :class="{
                                        'icon-glyphicons-basic-pause': element.isPlaying,
                                        'icon-glyphicons-basic-play': !element.isPlaying,
                                    }"
                                ></span>
                            </div>
                            <div
                                v-if="element.hide_video_controls"
                                @click.stop="toggleVimeoVolume(element)"
                                role="button"
                                :aria-pressed="element.videoVolumeOn"
                                aria-label="Toggle video volume"
                                class="flex-grow-0 p-1 mr-2 rounded pointer video-btn"
                                :class="{ 'text-white': !element.videoVolumeOn, 'icon-primary': element.videoVolumeOn }"
                            >
                                <span class="icon icon-glyphicons-basic-volume-up video-btn-icon align-middle"></span>
                            </div>
                            <div
                                v-if="element.hasCaptions && element.hide_video_controls"
                                @click.stop="toggleVimeoCaptions(element)"
                                role="button"
                                :aria-pressed="displayCaptions"
                                aria-label="Toggle video captions"
                                class="flex-grow-0 p-1 mr-1 rounded pointer video-btn"
                                :class="{ 'text-white': !displayCaptions, 'icon-primary': displayCaptions }"
                            >
                                <span class="icon icon-glyphicons-basic-subtitles video-btn-icon align-middle"></span>
                            </div>
                        </div>
                    </div>
                    <!-- Uploaded/Native Video -->
                    <div
                        v-if="element.element_type === 'video' && element.stored_file"
                        class="hide-overflow rounded-default"
                        :class="imageAndVideoElClassObj(element)"
                    >
                        <video
                            controls
                            :loop="element.loop_media"
                            :autoplay="element.autoplay_video"
                            class="video-js vjs-big-play-centered"
                            :class="{
                                'full-screen': element.display_full_screen,
                            }"
                            :id="'videoel' + element.id + _uid"
                            :style="{ width: `${element.display_width}%` }"
                            playsinline
                        >
                            <source :src="element.stored_file.signed_url" type="video/mp4" />
                            <track
                                v-if="element.stored_file.captions_vtt_text"
                                kind="captions"
                                :src="getCaptionsString(element.stored_file)"
                                label="captions on"
                                default
                            />
                            Your browser doesn't support embedded videos.
                        </video>
                        <!-- Video Control Buttons -->
                        <div
                            class="d-flex justify-content-end align-items-center video-btn-container"
                            :class="{
                                'position-fixed': element.display_full_screen,
                                'position-absolute': !element.display_full_screen,
                            }"
                            v-if="element.isLoaded"
                        >
                            <div
                                v-show="element.continueWasEmitted && element.continue_after_video_finished"
                                class="spinner-grow spinner-grow-md text-light mr-2"
                                role="status"
                            >
                                <span class="sr-only">Loading...</span>
                            </div>
                            <info-popover
                                v-if="!element.loop_media"
                                :content="getProgressBarHoverText(element)"
                                placement="top"
                            >
                                <div
                                    class="progress video-progress"
                                    :class="{ 'video-progress-clickable': !element.hide_video_controls }"
                                    @click="handleNativeVideoSeek($event, element, index)"
                                    ref="videoProgressBar"
                                >
                                    <div
                                        class="progress-bar bg-primary"
                                        :class="{ 'video-progress-ease': element.easeProgressBar }"
                                        role="progressbar"
                                        :style="{ width: element.videoProgressPercent + '%' }"
                                        aria-valuemin="0"
                                        aria-valuemax="100"
                                        aria-label="Video progress"
                                    ></div>
                                </div>
                            </info-popover>
                            <div
                                v-if="!element.hide_video_controls"
                                @click="rewindNativeVideo(element, 10)"
                                class="p-1 mr-2 rounded pointer video-btn"
                                role="button"
                            >
                                <span
                                    class="icon text-light video-btn-icon align-middle icon-glyphicons-basic-rewind-ten"
                                ></span>
                            </div>
                            <div
                                @click="togglePauseVideo(element)"
                                class="p-1 mr-2 rounded pointer video-btn"
                                role="button"
                            >
                                <span
                                    class="icon text-light video-btn-icon align-middle"
                                    :class="{
                                        'icon-glyphicons-basic-pause': element.isPlaying,
                                        'icon-glyphicons-basic-play': element.isPlaying === false,
                                    }"
                                ></span>
                            </div>
                            <div
                                @click.stop="toggleNativeVolume(element)"
                                role="button"
                                :aria-pressed="element.videoVolumeOn"
                                aria-label="Toggle video volume"
                                class="flex-grow-0 p-1 mr-2 rounded pointer video-btn"
                                :class="{ 'text-white': !element.videoVolumeOn, 'icon-primary': element.videoVolumeOn }"
                            >
                                <span class="icon icon-glyphicons-basic-volume-up video-btn-icon align-middle"></span>
                            </div>
                            <div
                                v-if="element.stored_file.captions_vtt_text"
                                @click.stop="toggleNativeCaptions($event)"
                                role="button"
                                :aria-pressed="displayCaptions"
                                aria-label="Toggle video captions"
                                class="flex-grow-0 p-1 mr-2 rounded pointer video-btn"
                                :class="{ 'text-white': !displayCaptions, 'icon-primary': displayCaptions }"
                            >
                                <span class="icon icon-glyphicons-basic-subtitles video-btn-icon align-middle"></span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <!-- Audio -->
            <div v-else-if="['audio', 'audio_ai'].includes(element.element_type) && element.stored_file" class="w-100">
                <div class="d-inline-block">
                    <audio
                        controls
                        :loop="element.loop_media"
                        :autoplay="element.autoplay_video && !element.wasPlayed"
                        :src="element.stored_file.signed_url"
                        @ended="audioEnded(element)"
                        @play="audioPlayed(element)"
                        :id="'audio-el-' + element.id"
                    >
                        Your browser does not support the
                        <code>audio</code> element.
                    </audio>
                    <div class="d-flex align-items-center">
                        <div class="flex-grow-1" v-if="element.accessibility_alt_text">
                            <button
                                class="btn btn-sm btn-link text-secondary py-0 my-1 px-1"
                                @click="toggleDisplayAudioTranscripts()"
                            >
                                <span class="align-middle mr-1">{{ labelsTranslated.showTranscript }}</span>
                                <span
                                    class="icon icon-100 align-middle"
                                    :class="{
                                        'icon-glyphicons-basic-chevron-up': displayTranscripts,
                                        'icon-glyphicons-basic-chevron-down': !displayTranscripts,
                                    }"
                                ></span>
                            </button>
                        </div>
                        <div v-if="element.element_type === 'audio_ai'" class="flex-grow-0 mx-1">
                            <info-popover :content="labelsTranslated.aiAudio" placement="top">
                                <ai-badge :enable-tooltip="false" label="AI-Generated"></ai-badge>
                            </info-popover>
                        </div>
                    </div>
                </div>
                <div
                    v-show="displayTranscripts"
                    class="text-secondary audio-transcript mb-1 mt-2 whitespace-pre"
                    style="font-size: 14px"
                    :id="'transcript' + element.id"
                    dir="auto"
                >
                    {{ element.accessibility_alt_text }}
                </div>
            </div>
            <!-- Rich Text -->
            <div v-else-if="element.element_type === 'text'">
                <typed-text
                    v-if="element.typing_animation_enabled"
                    v-model="element.content"
                    class="rich-text-content"
                ></typed-text>
                <div v-else v-html="element.content" class="rich-text-content"></div>
            </div>
            <!-- Downloadable File -->
            <div v-else-if="element.element_type === 'downloadable'">
                <a
                    v-if="element.stored_file"
                    class="btn btn-gray btn-sm global-theme-text"
                    target="_blank"
                    :download="element.stored_file.name"
                    :href="element.stored_file.signed_url"
                >
                    <span class="icon icon-glyphicons-basic-cloud-download icon-mr align-middle"></span>
                    <span v-if="element.content" class="align-middle">{{ element.content }}</span>
                    <span v-else>Download File</span>
                </a>
            </div>
            <!-- iframe -->
            <div
                v-else-if="element.element_type === 'iframe'"
                v-html="element.content"
                class="iframe-container"
                :class="{ 'disable-pointers': disableIframePointers }"
                :key="embedElementKey"
            ></div>
            <!-- PDF -->
            <div v-else-if="element.element_type === 'pdf'" :key="embedElementKey">
                <iframe
                    :src="element.stored_file.signed_url"
                    class="pdf-embed"
                    :class="{ 'disable-pointers': disableIframePointers }"
                    :style="{ 'min-height': element.height ? element.height + 'px' : '550px' }"
                ></iframe>
            </div>
            <div v-if="index !== elements.length - 1">
                <hr v-if="showHrDividers" class="my-3" />
                <div v-else class="element-spacer"></div>
            </div>
        </div>
    </div>
</template>

<script>
import http from "@/axios.js"
import YouTubeVideo from "@/components/YouTubeVideo"
import TypedText from "@/components/TypedText"
import videojs from "video.js"
import Player from "@vimeo/player"
import interact from "interactjs"
import debounce from "lodash.debounce"
import InfoPopover from "@/components/InfoPopover"
import AiBadge from "@/components/AiBadge"

// Have to import the css here for some reason, importing within a styles block
// doesn't seem to work.
import "video.js/dist/video-js.css"

const videoEndLeadTime = 0.3

export default {
    name: "RichContentDisplay",
    components: {
        YouTubeVideo,
        TypedText,
        InfoPopover,
        AiBadge,
    },
    props: {
        elements: {
            type: Array,
            default: function () {
                return []
            },
        },
        editable: {
            type: Boolean,
            default: false,
        },
        skipMediaToEnd: {
            type: Boolean,
            default: false,
        },
        closeFullScreenVideosAfterEnd: {
            type: Boolean,
            default: false,
        },
        showContinueButtonOnVideos: {
            type: Boolean,
            default: false,
        },
        continueBtnText: {
            type: String,
            default: "Continue",
        },
        showHrDividers: {
            type: Boolean,
            default: false,
        },
        extraElementClasses: {
            type: Array,
            required: false,
            default() {
                return []
            },
        },
    },
    data() {
        return {
            nativeVideoPlayers: [],
            vimeoVideoPlayers: [],
            interactResize: null,
            videoContinueLoading: false,
            // This property is used to force the PDF iframe element to refresh by incrementing it.
            embedElementKey: 0,
            // A list of IDs of video elements that have been loaded. Prevents certain initialization
            // functions from running more than once.
            loadedVideoElementIds: [],
        }
    },
    computed: {
        // Indicates whether all videos that are flagged as required before continuing
        // have finished yet.
        allRequiredVideosComplete() {
            return (
                this.elements.filter((el) => {
                    return el.play_video_before_continue && !el.videoComplete
                }).length == 0
            )
        },
        displayCaptions() {
            return this.$store.getters.displayCaptions
        },
        displayTranscripts() {
            return this.$store.getters.displayAudioTranscripts
        },
        resizeSelector() {
            return `[data-resize="${this._uid}"]`
        },
        themePrimaryColor() {
            return this.$store.getters.sessionThemePrimaryColor
        },
        labelsTranslated() {
            return this.$store.getters.sessionLabelsTranslated
        },
        // This is used to disable pointer-events on iframes elements during certain edit-actions.
        // Fixes an issue where drag-and-drop actions are blocked by the presence of iframes.
        disableIframePointers() {
            return this.$store.getters.editPanelModalIsOpen || this.$store.getters.layoutResizeIsActive
        },
    },
    watch: {
        allRequiredVideosComplete: function (newValue) {
            this.$emit("update:videoComplete", true)
        },
        editable: function (newValue) {
            if (newValue === false && this.interactResize) {
                // Make sure image resize mode is exited when edit mode is turned off.
                let vm = this
                this.elements.forEach((el) => {
                    vm.exitImageEditMode(el)
                })
            } else if (newValue === true && !this.interactResize) {
                // Initialize resize actions.
                this.initResizableElements()
            }
        },
        elements: function () {
            let vm = this
            // Initialize the hover status of each element so we can set it during variou
            // user actions.
            this.elements.forEach(function (element) {
                vm.$set(element, "isHover", false)
            })
        },
    },
    methods: {
        // Handle the completion of a video element.
        handleVideoComplete(element, preventAutoContinue = false) {
            this.$emit("video-complete")
            this.$set(element, "videoComplete", true)
            this.$set(element, "hasBeenPlayed", true)
            if (element.continue_after_video_finished && !preventAutoContinue) {
                this.emitVideoContinue(element)
                return
            }
            if (element.display_full_screen && this.closeFullScreenVideosAfterEnd) {
                this.closeFullScreen(element)
            }
        },
        // Signal to the parent component to continue to the next node.
        emitVideoContinue(element) {
            // Make sure the video hasn't already been completed to avoid
            // triggering these events twice.
            if (element.continueWasEmitted) {
                return
            } else {
                this.$set(element, "continueWasEmitted", true)
            }
            // Signal to the parent component to continue to the next node.
            this.$emit("continue")
        },
        // Handle a video almost ending - do this logic ahead of the actual video end
        // to reduce load time.
        videoAlmostEnding(element) {
            if (element.continue_after_video_finished && !this.skipMediaToEnd) {
                this.emitVideoContinue(element)
            }
        },
        // Restart all videos.
        restartVideos() {
            let embeddedVideoElements = this.elements.filter((el) => ["youtube", "vimeo"].includes(el.element_type))
            let nativeVideoElements = this.elements.filter((el) => el.element_type === "video")
            let vm = this
            embeddedVideoElements.forEach((el) => {
                vm.restartEmbeddedVideo(el)
            })
            nativeVideoElements.forEach((el) => {
                vm.restartNativeVideo(el)
            })
        },
        // Restart a video element.
        restartVideo(element) {
            if (["youtube", "vimeo"].includes(element.element_type)) {
                this.restartEmbeddedVideo(element)
            } else if (element.element_type == "video") {
                this.restartNativeVideo(element)
            }
        },
        // Restart an embedded video.
        restartEmbeddedVideo(element, index) {
            if (element.element_type == "youtube") {
                if (!index) {
                    index = this.elements.map((el) => el.id).indexOf(element.id)
                }
                // Need to also provide the index, as assigning refs within a for-loop
                // creates a list within the $refs object.
                let videoComponent = this.$refs["youtube-video" + element.id][index]
                videoComponent.restart()
            } else if (element.element_type == "vimeo") {
                var selector = document.querySelector("#vimeo" + element.id)
                var player = new Player(selector)
                player.setCurrentTime(0).then(function (seconds) {
                    player.play()
                })
            }

            element.videoComplete = false
            this.$emit("video-restart")
        },
        // Restart a native video from the beginning.
        restartNativeVideo(element) {
            let player = this.getNativeVideoPlayer(element)
            player.currentTime(0)
            // Freeze the height of the video container temporarily to avoid choppiness when
            // calling load() in the next line.
            let videoEl = document.getElementById(this.getNativeVideoElementId(element))
            videoEl.parentElement.style.height = videoEl.clientHeight + "px"
            // Call load to make sure the video is in a playable state. Without this, an error
            // is sometimes thrown, complaining about a call to play being interrupted by a call
            // to pause.
            player.load()
            element.videoComplete = false
            let vm = this
            player.play().then(() => {
                vm.$emit("video-restart")
                // Remove the video container height freeze.
                videoEl.parentElement.style.height = ""
            })
        },
        togglePauseVideo(element) {
            if (element.element_type == "vimeo") {
                this.togglePauseVimeo(element)
            } else if (element.element_type == "video") {
                this.togglePauseNative(element)
            }
        },
        // A function used to toggle a video player by clicking on it, when controls
        // are disabled on the video.
        togglePauseNative(element) {
            if (element.videoComplete) {
                this.restartNativeVideo(element)
                return
            }
            let player = this.getNativeVideoPlayer(element)
            let playerIsPaused = player.paused()
            if (playerIsPaused) {
                player.play()
            } else {
                player.pause()
            }
        },
        // Toggle the play/pause state of an embedded vimeo video.
        togglePauseVimeo(element) {
            if (element.videoComplete) {
                this.restartVideo(element)
                return
            }
            var selector = document.querySelector("#vimeo" + element.id)
            var player = new Player(selector)
            player.getPaused().then(function (paused) {
                if (paused) {
                    player.play()
                } else {
                    player.pause()
                }
            })
        },
        // Close fullscreen mode (for all videos).
        closeFullScreen(element) {
            element.display_full_screen = false
            if (element.element_type === "video") {
                this.closeNativeVideoPlayer(element)
            }
        },
        // Handle the video-continue button being pressed.
        continueToNext() {
            this.$emit("continue")
            this.videoContinueLoading = true
        },
        // Get the DOM element ID for a given native video element.
        getNativeVideoElementId(element) {
            // Appending the unique ID for the component fixes and issue with the drag and drop
            // node editor where multiple copies of the same component are rendered simultaneously.
            return "videoel" + element.id + this._uid
        },
        // Fetch an instance of a native video player.
        getNativeVideoPlayer(element) {
            let selector = `#${this.getNativeVideoElementId(element)}`
            return videojs(document.querySelector(selector))
        },
        // Exit fullscreen mode for a native video.
        closeNativeVideoPlayer(element) {
            // Need to manually remove a set of classes that are applied by videojs (these aren't
            // reactive with vue) in order to remove the fullscreen styling.
            const elementId = this.getNativeVideoElementId(element)
            let videoElement = document.getElementById(elementId)
            videoElement.classList.remove("full-screen", elementId + "-dimensions")

            // This fixes an issue on iOS devices where collapsed fullscreen videos lose their
            // height (i.e. show up as a thin rectangle). Running this code on other devices
            // makes the video unplayable, so only run on iOS.
            const userAgent = navigator.userAgent
            let isIphone = /iPad|iPhone|iPod/.test(userAgent) && !window.MSStream
            if (isIphone) {
                window.setTimeout(() => {
                    element.player.fluid(true)
                }, 100)
            }
        },
        // Initialize a native video player for an element.
        initializeNativeVideoPlayer(element) {
            let selector = `#${this.getNativeVideoElementId(element)}`
            let videoPlayerElement = document.querySelector(selector)
            let player = videojs(videoPlayerElement, {
                fluid: !element.display_full_screen,
                preload: "auto",
                textTrackSettings: false,
                loop: element.loop_media,
                autoplay: element.autoplay_video,
                controls: false,
            })

            // Initialize player properties on the element.
            this.$set(element, "isPlaying", element.autoplay_video)
            this.$set(element, "videoVolumeOn", true)
            this.$set(element, "player", player)
            this.$set(element, "easeProgressBar", true)

            // Add skin class for custom styles.
            player.addClass("vjs-skin")
            // Save the player so we can dispose it later on.
            this.nativeVideoPlayers.push(player)

            // Bind callbacks to player events.
            let vm = this
            player.ready(function () {
                vm.$set(element, "isLoaded", true)
                vm.$set(element, "videoProgressPercent", 0)

                // Listen for the "timeupdate" event, which is triggered every second.
                player.on("timeupdate", function () {
                    // Get the current time and duration of the video.
                    let currentTime = player.currentTime()
                    let duration = player.duration()
                    // Check if the video is almost ending.
                    if (duration - currentTime <= videoEndLeadTime) {
                        vm.videoAlmostEnding(element)
                    }
                    // Update the current progress of the video.
                    element.videoProgressPercent = Math.round((currentTime * 100) / duration)
                })

                player.on("ended", function () {
                    vm.handleVideoComplete(element)
                })

                player.on("loadedmetadata", function () {
                    // This condition makes sure these actions only run once. The loadedmetdata event
                    // is triggered when restarting videos, in which case skipping to the end of the
                    // video needs to be prevented.
                    if (vm.loadedVideoElementIds.includes(element.id)) {
                        return
                    }
                    vm.loadedVideoElementIds.push(element.id)
                    
                    // Don't skip to the end of the video if the video itself is used as the trigger for
                    // continuing/moving forward.
                    if (vm.skipMediaToEnd && !element.continue_after_video_finished) {
                        // Skip to the end of the video.
                        player.currentTime(player.duration())
                        vm.handleVideoComplete(element, true)
                    }
                })

                player.on("play", function () {
                    vm.$set(element, "isPlaying", true)
                    vm.$set(element, "hasStarted", true)

                    // Once the video has started playing, turn off the autoplay flag. This prevents
                    // an issue where the drag and drop actions involving videos create copies (drag ghosts)
                    // of the video that play in the background and can't be stopped.
                    vm.$set(element, "autoplay_video", false)
                })
                player.on("pause", function () {
                    vm.$set(element, "isPlaying", false)
                })

                // Handle captions being turned on and off using the videojs player.
                // Persist the captions state in the store to be applied to all
                // video players throughout the session.
                player.on("texttrackchange", function () {
                    let tracks = player.textTracks()
                    let activeTrack = null
                    for (let i = 0; i < tracks.length; i++) {
                        let track = tracks[i]
                        if (track.mode == "showing") {
                            activeTrack = track
                            break
                        }
                    }
                    if (activeTrack && !vm.displayCaptions) {
                        vm.$store.dispatch("setDisplayCaptions", true)
                    } else if (!activeTrack && vm.displayCaptions) {
                        vm.$store.dispatch("setDisplayCaptions", false)
                    }
                })

                vm.setNativeCaptionsDisplayState()

                if (vm.$store.getters.preventVideoAutoplay) {
                    player.pause()
                    vm.$set(element, "isPlaying", false)
                }

                // Try to play the video if autoplay is turned on, in order to gain visibility into
                // whether the user's browser blocks or allows the autoplay (by catching the error).
                // Set the 'isPlaying' property accordingly.
                if (element.autoplay_video) {
                    player.play().catch(() => {
                        vm.$set(element, "isPlaying", false)
                    })
                }
            })
        },
        // Initialize a vimeo video player for an element.
        initializeVimeoVideoPlayer(element) {
            let playerConfig = {
                url: element.content,
                responsive: true,
                controls: !element.hide_video_controls,
                autoplay: element.autoplay_video,
                color: this.themePrimaryColor.substring(1),
                loop: element.loop_media,
                byline: false,
                dnt: true,
                title: false,
                muted: false,
                quality: "720p",
            }
            let player = new Player("vimeo" + element.id, playerConfig)

            // Initialize player properties on the element,
            this.$set(element, "isPlaying", element.autoplay_video)
            this.$set(element, "player", player)
            this.$set(element, "videoVolumeOn", true)

            // This is a slight hack that allows us to determine whether the video
            // is 360-degrees. If the promise resolves, set the player quality to 'auto',
            // which will serve the video in the highest quality available supported by the user's
            // internet connection.
            player
                .getCameraProps()
                .then(function () {
                    player.setQuality("auto")
                })
                .catch(function () {
                    // pass
                })

            let vm = this
            player.on("loaded", function () {
                vm.$set(element, "isLoaded", true)
                vm.$emit("image-loaded")
            })

            // Check if the player has a captions tracks.
            player.getTextTracks().then(function (tracks) {
                if (tracks.length > 0) {
                    vm.$set(element, "hasCaptions", true)
                } else {
                    vm.$set(element, "hasCaptions", false)
                }
            })

            vm.$set(element, "videoProgressPercent", 0)
            // Listen for the "timeupdate" event, which is triggered every second.
            player.on("timeupdate", function () {
                // Get the current time and duration of the video
                player.getDuration().then(function (duration) {
                    player.getCurrentTime().then(function (currentTime) {
                        // Check if the video is almost ending.
                        if (duration - currentTime <= videoEndLeadTime) {
                            vm.videoAlmostEnding(element)
                        }
                        element.videoProgressPercent = Math.round((currentTime * 100) / duration)
                    })
                })

                // This fixes a Safari issue where the "ended" event doesn't fire.
                player.getEnded().then(function (ended) {
                    if (ended) {
                        vm.handleVideoComplete(element)
                    }
                })
            })

            player.on("ended", function () {
                vm.handleVideoComplete(element)
            })

            // Save the player so we can dispose it later on.
            this.vimeoVideoPlayers.push(player)

            if (vm.skipMediaToEnd) {
                // Skip to the end of the video.
                player.getDuration().then(function (duration) {
                    player.setCurrentTime(duration - 1)
                    player.pause()
                })
                this.handleVideoComplete(element, true)
            }

            player.on("pause", function (data) {
                if (data.percent < 0.99) {
                    element.isPlaying = false
                }
            })
            player.on("play", function () {
                element.isPlaying = true
                vm.$set(element, "hasStarted", true)

                // Once the video has started playing, turn off the autoplay flag. This prevents
                // an issue where the drag and drop actions involving videos create copies (drag ghosts)
                // of the video that play in the background and can't be stopped.
                vm.$set(element, "autoplay_video", false)
            })

            // Handle captions being turned on and off using the vimeo player controls.
            // Persist the captions state in the store to be applied to all
            // video players throughout the session.
            player.on("cuechange", function (event) {
                if (event.language) {
                    vm.$store.dispatch("setDisplayCaptions", true)
                } else {
                    vm.$store.dispatch("setDisplayCaptions", false)
                }
            })

            // This fixes an issue in Chrome where videos initially play with the volume
            // turned off.
            player.setVolume(1).then(function () {
                element.videoVolumeOn = true
            })

            this.setVimeoCaptionsDisplayState(element)

            if (this.$store.getters.preventVideoAutoplay) {
                player.pause()
                vm.$set(element, "isPlaying", false)
            }
        },
        // Toggle the volume (mute or unmute) a vimeo video.
        toggleVimeoVolume(element) {
            let targetVolume = element.videoVolumeOn ? 0 : 1
            element.player.setVolume(targetVolume).then(function (volume) {
                element.videoVolumeOn = volume != 0
            })
        },
        // Toggle the appearance of Vimeo captions.
        toggleVimeoCaptions(element) {
            this.$store.dispatch("toggleDisplayCaptions")
            this.setVimeoCaptionsDisplayState(element)
        },
        // Set the appearance of Vimeo captions based on the stored captions
        // setting.
        setVimeoCaptionsDisplayState(element) {
            let vm = this
            if (vm.displayCaptions) {
                let videoUrlParams = element.content.split("?")
                if (videoUrlParams.length > 1) {
                    // Turn on captions for the language specified in Vimeo URL query parameters.
                    const urlSearchParams = new URLSearchParams(videoUrlParams[1])
                    const params = Object.fromEntries(urlSearchParams.entries())
                    if (params.texttrack) {
                        element.player.enableTextTrack(params.texttrack)
                        return
                    }
                }
                // Turn on the first captions track available on the video.
                element.player.getTextTracks().then(function (tracks) {
                    if (tracks.length > 0) {
                        element.player.enableTextTrack(tracks[0].language)
                    }
                })
            } else {
                element.player.disableTextTrack()
            }
        },
        // Handle the video progress bar being clicked. If video controls are enabled,
        // skip to the position relative to where the progress bar was clicked.
        handleNativeVideoSeek(event, element, index) {
            if (element.hide_video_controls) {
                return
            }
            let progressContainer = this.$refs.videoProgressBar[index]
            // Get the horizontal position of the click.
            let clickOffsetX = event.offsetX
            let progressBarWidth = progressContainer.offsetWidth
            let duration = element.player.duration()
            // Determine the new time to seek to based on the duration of the video
            // and the x position of the click relative to the width of the progress bar.
            let newTime = (clickOffsetX / progressBarWidth) * duration
            // Update the current video time.
            element.player.currentTime(newTime)
            // Temporarily turn off the progress bar easing so the new position visually
            // changes quickly.
            element.easeProgressBar = false
            element.videoProgressPercent = Math.round((newTime * 100) / duration)
            window.setTimeout(() => (element.easeProgressBar = true), 200)
            // If the video has completed, make sure it's playing.
            if (element.videoComplete) {
                element.videoComplete = false
                element.player.play()
                this.$emit("video-restart")
            }
        },
        // Rewind a video by a number of seconds.
        rewindNativeVideo(element, seconds) {
            let newTime = Math.min(0, element.player.currentTime() - seconds)
            element.player.currentTime(newTime)
            // If the video has completed, make sure it's playing.
            if (element.videoComplete) {
                element.videoComplete = false
                element.player.play()
                this.$emit("video-restart")
            }
        },
        // Returns the text to show when hovering over the video playback progress bar.
        getProgressBarHoverText(element) {
            return element.hide_video_controls ? "Seeking is disabled for this video" : ""
        },
        // Toggle the appearance of native video captions.
        toggleNativeCaptions() {
            this.$store.dispatch("toggleDisplayCaptions")
            this.setNativeCaptionsDisplayState()
        },
        // Toggle the volume (mute or unmute) a native video.
        toggleNativeVolume(element) {
            element.videoVolumeOn = !element.videoVolumeOn
            element.player.muted(!element.videoVolumeOn)
        },
        // Set the appearance of native video captions based on the stored captions setting.
        setNativeCaptionsDisplayState() {
            let vm = this
            this.nativeVideoPlayers.forEach((player) => {
                // Get all text tracks for the current player.
                var tracks = player.textTracks()
                for (var i = 0; i < tracks.length; i++) {
                    var track = tracks[i]
                    if (track.kind === "captions" && vm.displayCaptions) {
                        track.mode = "showing"
                    } else if (track.kind === "captions" && !vm.displayCaptions) {
                        track.mode = "hidden"
                    }
                }
            })
        },
        // Skip a video element to the end.
        skipVideoToEnd(element) {
            if (element.element_type == "video") {
                element.player.currentTime(element.player.duration())
            } else if (element.element_type == "vimeo") {
                element.player.getDuration().then(function (duration) {
                    // Skip to just before the video ends for Vimeo to make
                    // sure end-of-video events get fired.
                    element.player.setCurrentTime(duration - 0.25)
                })
            } else if (element.element_type == "youtube") {
                this.$refs["youtube-video" + element.id][0].skipVideo()
            }
            this.handleVideoComplete(element)
        },
        imageLoaded() {
            this.$emit("image-loaded")
        },
        initializeAudioPlayer(element) {
            // Don't skip to the end of the audio if the audio itself is used as the trigger for
            // continuing/moving forward. Otherwise, revisiting the screen would just immediately
            // move the learner forward.
            if (this.skipMediaToEnd && !element.continue_after_video_finished) {
                let audio = document.getElementById("audio-el-" + element.id)
                audio.addEventListener("loadedmetadata", function () {
                    audio.currentTime = audio.duration
                })
            }
        },
        audioEnded(element) {
            this.$emit("video-complete")
            this.$set(element, "videoComplete", true)
            if (element.continue_after_video_finished) {
                this.emitVideoContinue(element)
            }
        },
        audioPlayed(element) {
            // Set a property indicating that the audio clip started playing. This is used
            // to fix a bug where audio is duplicated during screen editing.
            this.$set(element, "wasPlayed", true)
        },
        updateElement(element) {
            http.put("/api/elements/" + element.id + "/", element)
            this.$emit("element-updated")
        },
        setElementWidth: debounce(async function (elementId, width, units) {
            let element = this.elements.find((c) => c.id == elementId)
            element.display_width = width
            element.display_width_units = units
            this.updateElement(element)
        }, 200),
        hoverElement(element) {
            element.isHover = true
        },
        unhoverElement(element) {
            element.isHover = false
        },
        setElementJustify(element, justify) {
            element.display_justify = justify
            this.updateElement(element)
        },
        skipVideos() {
            let vm = this
            this.elements
                .filter((e) => ["video", "vimeo", "youtube"].includes(e.element_type))
                .forEach((e) => {
                    vm.skipVideoToEnd(e)
                })
        },
        resetEmbedElements() {
            // Increase the key of the embedded iframe elements to fully reset their display.
            this.embedElementKey += 1
        },
        toggleDisplayAudioTranscripts() {
            this.$store.commit("setDisplayAudioTranscripts", !this.displayTranscripts)
        },
        toggleExpandedImage(element, state) {
            if (this.editable) {
                // Don't open the image in expanded view if edit mode is active.
                return
            }
            this.$set(element, "isExpanded", state)
        },
        imageStyleBlock(element) {
            if (element.isExpanded) {
                return {}
            }
            return {
                width: `${element.display_width}${element.display_width_units}`,
            }
        },
        // Returns an object of classes to be applied to a given video or image element.
        // This is a bit of a hack to allow us to merge the extra classes passed into this component
        // as a prop with the other classes defined by this component.
        imageAndVideoElClassObj(element) {
            let classes = {
                "image-resizable": this.editable && !element.isExpanded && element.editIsActive,
                "image-inline": !element.isExpanded,
                "rounded-default": !element.editIsActive,
            }
            // Apply extra provided element classes if element isn't being actively resized/edited.
            if (!element.editIsActive) {
                this.extraElementClasses.forEach((extraClass) => {
                    classes[extraClass] = true
                })
            }
            return classes
        },
        // Returns an object of classes to be applied to a video cover for an element.
        // This is a bit of a hack to allow us to merge the extra classes passed into this component
        // as a prop with the other classes defined by this component.
        videoCoverClassObj(element) {
            let classes = {
                "video-overlay-darken": element.videoComplete && !element.loop_media,
                "full-screen": element.display_full_screen,
                "video-overlay": !element.display_full_screen,
                "rounded-default": !element.display_full_screen,
            }
            this.extraElementClasses.forEach((extraClass) => {
                classes[extraClass] = true
            })
            return classes
        },
        // Enter edit mode for an image.
        enterImageEditMode(element) {
            this.$store.commit("setImageEditModeIsActive", true)
            this.$set(element, "isHover", false)
            this.$set(element, "editIsActive", true)
            this.interactResize.resizable(true)
        },
        // Exit edit mode for an image.
        exitImageEditMode(element) {
            this.$store.commit("setImageEditModeIsActive", false)
            element.editIsActive = false
            this.interactResize.resizable(false)
        },
        // Initialize resize actions on resizable elements.
        initResizableElements() {
            let vm = this
            this.interactResize = interact(this.resizeSelector).resizable({
                // Resize from all edges and corners.
                edges: { left: true, right: true, bottom: true, top: true },
                // Allow for a large margin for drag handles.
                margin: 30,
                listeners: {
                    move(event) {
                        let target = event.target
                        // Capture the new width as a percentage of its parent.
                        let percentWidth = (event.rect.width * 100) / target.parentElement.offsetWidth
                        if (percentWidth > 100) {
                            percentWidth = 100
                        }
                        target.style.width = percentWidth + "%"
                        // Store the new width server side.
                        let elementId = event.target.getAttribute("data-id")
                        vm.setElementWidth(elementId, Math.round(percentWidth), "%")
                    },
                },
                modifiers: [
                    interact.modifiers.restrictSize({
                        min: { width: 100, height: 50 },
                    }),
                    interact.modifiers.aspectRatio({
                        ratio: "preserve",
                    }),
                ],
                inertia: true,
            })
            // Start with resizing disabled - user has to press the edit button to begin resizing.
            this.interactResize.resizable(false)
        },
        // Returns a string of video captions (in vtt format) in a videojs-friendly format.
        getCaptionsString(storedFile) {
            return `data:text/vtt;charset=utf-8,${encodeURIComponent(storedFile.captions_vtt_text)}`
        },
    },
    mounted() {
        let vm = this

        let videoElements = this.elements.filter((el) => el.element_type == "video")
        let vimeoElements = this.elements.filter((el) => el.element_type == "vimeo")
        let youtubeElements = this.elements.filter((el) => el.element_type == "youtube")
        let audioElements = this.elements.filter((el) => el.element_type == "audio")

        // Initialize native video players, if video elements are present.
        videoElements.forEach((element) => {
            vm.initializeNativeVideoPlayer(element)
        })
        // Initialize embedded vimeo players, if present.
        vimeoElements.forEach((element) => {
            vm.initializeVimeoVideoPlayer(element)
        })
        // Initialize youtube elements.
        youtubeElements.forEach((element) => {
            // There's no way to hide player controls on youtube embeds, so make sure this
            // setting is turned off to avoid showing our simplified controls.
            element.hide_video_controls = false
        })
        audioElements.forEach((element) => {
            vm.initializeAudioPlayer(element)
        })

        // Initialize the hover status of each element so we can set it during various
        // user actions.
        this.elements.forEach(function (element) {
            vm.$set(element, "isHover", false)
        })

        if (this.editable) {
            this.initResizableElements()
        }
    },
    destroyed() {
        // Dispose of all players.
        this.nativeVideoPlayers.forEach((player) => {
            if (player) {
                player.dispose()
            }
        })
        this.vimeoVideoPlayers.forEach((player) => {
            if (player) {
                player.destroy()
            }
        })
    },
}
</script>

<style lang="scss" scoped>
@import "@/assets/styles/variables.scss";
@import "@/assets/styles/video.scss";

$drag-offset: 4px;
$video-btn-bg: #000000b3;

.image-inline {
    max-width: 100%;
    height: auto !important;
}

.image-expanded {
    cursor: zoom-out;
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.8);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1100;
    transition: all 0.3s;
    .image-container {
        max-width: 94%;
        max-height: 94%;
        object-fit: contain;
        border-radius: $border-radius-sm;
        height: 100%;
        img {
            object-fit: contain;
            border-radius: $border-radius-sm;
            height: 100%;
            max-width: 100%;
        }
    }
    animation: fade-in 0.3s 1;
}

@keyframes fade-in {
    from,
    to {
        background-color: rgba(0, 0, 0, 0.5);
    }
    100% {
        background-color: rgba(0, 0, 0, 0.8);
    }
}

.close-button {
    position: absolute;
    top: 3px;
    right: 5px;
    font-size: 25px;
    color: #fff;
    padding: 1px;
    cursor: pointer;
}

.image-resizable {
    box-shadow: 0 0 0 0.12rem $primary;
}

.resize-handle {
    width: 8.5px;
    height: 8.5px;
    background-color: $primary;
    position: absolute;
}

$resize-handle-offset: 50%;

.handle-top-left {
    top: 0;
    left: 0;
    transform: translate(-$resize-handle-offset, -$resize-handle-offset);
}

.handle-top-right {
    top: 0;
    right: 0;
    transform: translate($resize-handle-offset, -$resize-handle-offset);
}

.handle-bottom-left {
    bottom: 0;
    left: 0;
    transform: translate(-$resize-handle-offset, $resize-handle-offset);
}

.handle-bottom-right {
    bottom: 0;
    right: 0;
    transform: translate($resize-handle-offset, $resize-handle-offset);
}

.element-toolbar {
    position: absolute;
    right: -3px;
    top: -3px;
    margin-top: 12px;
    margin-right: 10px;
    background: transparent;
    .btn {
        opacity: 0;
        transition: opacity 0.1s;
        span {
            opacity: 1;
        }
    }
    &.visible {
        .btn {
            opacity: 0.92;
        }
    }
}

.hide-overflow {
    overflow: hidden;
}

.video-btn-container {
    z-index: 20;
    bottom: 15px;
    right: 15px;
}

.vimeo-preloaded {
    min-height: 300px;
}

.video-btn {
    background-color: $video-btn-bg;
    &:hover {
        transform: scale(1.08);
    }
    &:active {
        transform: scale(1);
    }
    transition: transform 0.15s ease-in-out;
}

.video-btn-icon {
    font-size: 26px;
}

.centered-play-pause-icon {
    color: #ffffff;
    z-index: 16;
    font-size: 65px;
    cursor: pointer;
    &:hover {
        scale: 1.05;
    }
    transition: $transition-base;
}

.play-pause-btn {
    &:active {
        scale: 0.85 !important;
    }
    opacity: 0;
    transition: $transition-base;
    z-index: 15 !important;
    background: transparent !important;
}

.opacity-1 {
    opacity: 1 !important;
}

.spinner-grow-md {
    width: 1.5rem;
    height: 1.5rem;
}

.pdf-embed {
    width: 100%;
    height: 100%;
    border: none;
}

.video-progress {
    @media (min-width: $breakpoint-sm) {
        width: 160px;
    }
    width: 130px;
    height: 10px;
    background-color: $video-btn-bg !important;
    margin-right: 20px;
}

.video-progress-clickable {
    cursor: pointer;
    &:hover {
        transform: scale(1.05);
    }
    transition: $transition-base;
}

.video-progress-ease {
    transition: width 0.3s linear !important;
}

.disable-pointers {
    pointer-events: none;
}

.audio-transcript {
    padding-left: 14px;
    border-left: 2px solid $gray;
}

.element-spacer {
    margin-top: 1.5rem;
}
</style>

<style lang="scss">
@import "@/assets/styles/variables.scss";

.iframe-container {
    overflow: hidden;
    iframe {
        max-width: 100% !important;
    }
}

.vjs-skin {
    .vjs-progress-holder {
        border-radius: 0.25rem;
    }

    .vjs-control-bar {
        background: #4e4e4e7a;
        padding-left: 1em;
        padding-right: 1em;
        font-size: 150%;
    }

    .vjs-theme-forest .vjs-progress-holder {
        border-radius: 0.2em;
        height: 0.5em;
        margin: 0;
    }

    .vjs-volume-level,
    .vjs-play-progress,
    .vjs-slider-bar {
        background: $primary;
        border-radius: $border-radius-lg;
    }

    .vjs-load-progress,
    .vjs-load-progress div,
    .vjs-play-progress {
        border-radius: $border-radius-lg;
    }

    .vjs-loading-spinner {
        display: none;
    }

    .vjs-big-play-button {
        border: none;
        display: none;
    }

    .vjs-slider:focus,
    .vjs-control:focus {
        text-shadow: none;
        -webkit-box-shadow: none;
        box-shadow: none;
    }

    .vjs-poster {
        background-color: white;
        background-size: cover;
    }

    // Hack fix to remove a black line that shows up at the right hand and bottom
    // edge of the videos.
    .vjs-tech {
        width: 101% !important;
        height: 101% !important;
    }

    .vjs-button {
        &:active {
            scale: 0.85;
        }
        transition: $transition-base;
    }
    
    .vjs-text-track-display {
        margin-bottom: 35px !important;
    }
}

.video-js:hover .vjs-big-play-button,
.video-js .vjs-big-play-button:focus {
    background-color: rgba(43, 51, 63, 0.62);
    transition: all ease-in-out 0s;
}

.video-js {
    background: #fff;
    height: auto;
    &:not(.full-screen) {
        .vjs-text-track-cue {
            font-size: 24px !important;
        }
    }
}

.replay-continue-btns {
    z-index: 16 !important;
    opacity: 1 !important;
    background: transparent !important;
}

.vimeo-fullscreen {
    div {
        position: initial !important;
    }
}

.rounded-default {
    border-radius: $border-radius-lg;
}

// Need to keep this class present even though it's already
// defined elsewhere.
.full-screen {
    position: fixed;
    width: 100%;
    height: 100%;
    left: 0;
    top: 0;
    background: #212121;
    z-index: 10;
}
</style>